import type { CSSProperties, ReactNode } from "react";

import { initialsOf } from "../mockData";
import "./frames.css";

/**
 * Marcos de las maquetas: pantallas de Torneos123 dibujadas en HTML/CSS con
 * datos de ejemplo. No dependen del tema de la app (usan tokens --mk-* que
 * copian «Noche de finales») y todo se mide en `em`, así que un solo
 * font-size escala la maqueta completa.
 */

export function MockFigure({
  label,
  className = "",
  caption = true,
  style,
  children,
}: {
  /** Descripción para lectores de pantalla (la maqueta es una imagen). */
  label: string;
  className?: string;
  caption?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <figure className={`t123-mock ${className}`.trim()} role="img" aria-label={label} style={style}>
      <div className="t123-mock__stage" aria-hidden="true">
        {children}
      </div>
      {caption ? <figcaption className="t123-mock__caption">Datos de ejemplo</figcaption> : null}
    </figure>
  );
}

export function PhoneFrame({ className = "", children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`mk-phone ${className}`.trim()}>
      <div className="mk-phone__notch" />
      <div className="mk-phone__screen">{children}</div>
    </div>
  );
}

export function PanelFrame({
  title,
  badge,
  className = "",
  children,
}: {
  title: string;
  badge?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`mk-panel ${className}`.trim()}>
      <div className="mk-panel__bar">
        <span className="mk-panel__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="mk-panel__title">{title}</span>
        {badge ? <span className="mk-panel__badge">{badge}</span> : null}
      </div>
      <div className="mk-panel__body">{children}</div>
    </div>
  );
}

export function PaperSheet({ className = "", children }: { className?: string; children: ReactNode }) {
  return <div className={`mk-sheet ${className}`.trim()}>{children}</div>;
}

export function Toast({ title, body, when = "ahora", className = "" }: { title: string; body: string; when?: string; className?: string }) {
  return (
    <div className={`mk-toast ${className}`.trim()}>
      <span className="mk-toast__icon">✓</span>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
      <small>{when}</small>
    </div>
  );
}

export function NotificationCard({
  app = "Torneos123",
  when,
  title,
  body,
  iconSrc,
  className = "",
  style,
}: {
  app?: string;
  when: string;
  title: string;
  body: string;
  iconSrc?: string;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div className={`mk-notif ${className}`.trim()} style={style}>
      <div className="mk-notif__head">
        {iconSrc ? <img src={iconSrc} alt="" width={20} height={20} /> : <span className="mk-notif__logo" />}
        <span>{app}</span>
        <small>{when}</small>
      </div>
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

export function Avatar({ name, className = "" }: { name: string; className?: string }) {
  return <span className={`mk-avatar ${className}`.trim()}>{initialsOf(name)}</span>;
}

export function Pill({ tone = "neutral", children }: { tone?: "neutral" | "lime" | "ok" | "warn" | "bad"; children: ReactNode }) {
  return <span className={`mk-pill mk-pill--${tone}`}>{children}</span>;
}
